import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { registerPlugin } from '@capacitor/core';
import LocationConsentService from './LocationConsentService';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
const BG_WATCHER_STORAGE_KEY = 'okio_bg_watcher_id_v1';

const isNative = () => {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
};

const getStoredWatcherId = () => {
  try { return localStorage.getItem(BG_WATCHER_STORAGE_KEY); } catch { return null; }
};

const setStoredWatcherId = (watcherId) => {
  try {
    if (watcherId == null || watcherId === '') {
      localStorage.removeItem(BG_WATCHER_STORAGE_KEY);
      return;
    }
    localStorage.setItem(BG_WATCHER_STORAGE_KEY, String(watcherId));
  } catch {}
};

const getKnownWatcherId = (explicitWatcherId) => {
  if (explicitWatcherId != null && explicitWatcherId !== '') return explicitWatcherId;
  try {
    if (window.__okio_bg_watcher_id__) return window.__okio_bg_watcher_id__;
  } catch {}
  return getStoredWatcherId();
};

export const LocationService = {
  async checkPermissions() {
    if (!isNative()) return 'granted';
    try {
      const perm = await Geolocation.checkPermissions();
      return perm?.location || perm?.coarseLocation || 'prompt';
    } catch {
      return 'prompt';
    }
  },

  async requestPermissions() {
    if (!isNative()) return true;
    // Block the OS runtime permission prompt until the prominent disclosure
    // has been accepted. This is the single gate that prevents ANY code path
    // from triggering the Android permission dialog before the disclosure.
    if (!LocationConsentService.hasAccepted()) return false;
    try {
      const perm = await Geolocation.requestPermissions();
      const status = perm?.location || perm?.coarseLocation || 'granted';
      return status === 'granted' || status === 'limited';
    } catch {
      return false;
    }
  },

  async getCurrentPosition(options = { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }) {
    if (isNative()) {
      const permOk = await this.requestPermissions();
      if (!permOk) throw new Error('Location permission not granted');
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: options.timeout || 10000 });
      return { lat: pos.coords.latitude, lng: pos.coords.longitude, heading: pos.coords.heading };
    }

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude, heading: position.coords.heading }),
        (error) => reject(error),
        options
      );
    });
  },

  async watchPosition(onUpdate, onError, options = { enableHighAccuracy: true }) {
    if (isNative()) {
      const permOk = await this.requestPermissions();
      if (!permOk) throw new Error('Location permission not granted');
      const id = await Geolocation.watchPosition({ enableHighAccuracy: true }, (pos, err) => {
        if (err) { onError && onError(err); return; }
        if (!pos) return;
        // Pass a Position-like object for compatibility with web code
        onUpdate && onUpdate({
          coords: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            altitude: pos.coords.altitude,
            speed: pos.coords.speed,
            heading: pos.coords.heading
          },
          timestamp: pos.timestamp || Date.now()
        });
      });
      return { clear: async () => { await Geolocation.clearWatch({ id }); } };
    }

    if (!navigator.geolocation) throw new Error('Geolocation not supported');
    const id = navigator.geolocation.watchPosition(
      (position) => onUpdate && onUpdate(position),
      (error) => onError && onError(error),
      options
    );
    return { clear: () => { try { navigator.geolocation.clearWatch(id); } catch {} } };
  }
};

// Background tracking — requestPermissions is false so the OS prompt is never
// triggered implicitly. The caller must ensure foreground + background location
// permissions have been granted via the disclosure flow before calling start().
export const BackgroundLocation = {
  async start(options = { distanceFilter: 25 }) {
    if (!isNative()) throw new Error('Background tracking only on native');
    const existingWatcherId = getKnownWatcherId();
    if (existingWatcherId) {
      try { await BackgroundGeolocation.removeWatcher({ id: existingWatcherId }); } catch {}
      try { window.__okio_bg_watcher_id__ = null; } catch {}
      setStoredWatcherId(null);
    }
    const watcherId = await BackgroundGeolocation.addWatcher(
      {
        backgroundMessage: 'Tracking your trail... stop to save battery.',
        backgroundTitle: 'Okio Tracking',
        requestPermissions: false,
        stale: false,
        distanceFilter: options.distanceFilter ?? 25,
      },
      (location, error) => {
        try {
          if (error) {
            if (error.code === 'NOT_AUTHORIZED') {
              BackgroundGeolocation.openSettings();
            }
            return;
          }
          window.dispatchEvent(new CustomEvent('okio:bg_location', { detail: location }));
        } catch {}
      }
    );
    try { window.__okio_bg_watcher_id__ = watcherId; } catch {}
    setStoredWatcherId(watcherId);
    return watcherId;
  },
  async stop(watcherId) {
    const knownWatcherId = getKnownWatcherId(watcherId);
    if (!knownWatcherId) return;
    try { await BackgroundGeolocation.removeWatcher({ id: knownWatcherId }); } catch {}
    try {
      if (!watcherId || window.__okio_bg_watcher_id__ === knownWatcherId) {
        window.__okio_bg_watcher_id__ = null;
      }
    } catch {}
    if (!watcherId || String(watcherId) === String(knownWatcherId)) {
      setStoredWatcherId(null);
    }
  },
  getStoredWatcherId() {
    return getKnownWatcherId();
  }
};


